<template>
  <div class="hstack align-items-center justify-content-between gap-3 border rounded px-3 flex-shrink-0">
    <div class="text-muted fs-8 flex-shrink-0">
      {{ label }}
    </div>

    <input v-model.number="value" type="range" class="form-range" min="1" max="10" step="1" @change="handleChange" />
    <div class="flex-shrink-0 fs-7 text-muted min-badge">{{ value }} {{ $t('flexigo.common.min') }}</div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { createPreferenceKey } from '../../../../store/modules/userPreferences';
import companies from '../../../../services/companies';

export default {
  name: 'OtpThresholdSlider',
  props: {
    threshold: {
      type: Number,
      default: 5,
    },
    label: {
      type: String,
    },
  },
  data() {
    return {
      value: 5,
      isInitialized: false,
      preferenceKey: createPreferenceKey('otp', 'threshold'), // 'otp.threshold'
    };
  },
  async mounted() {
    await this.initializeThreshold();
  },
  methods: {
    ...mapActions('userPreferences', ['getPreference', 'savePreference']),

    async initializeThreshold() {
      try {
        // Try to load saved user preference from store first
        const savedThreshold = await this.getPreference({
          key: this.preferenceKey,
          defaultValue: null,
        });

        // Ensure savedThreshold is a number, not a string
        const numericThreshold = savedThreshold !== null ? Number(savedThreshold) : null;

        if (numericThreshold && numericThreshold > 0 && !Number.isNaN(numericThreshold)) {
          // Use saved user preference
          this.value = numericThreshold;
        } else {
          // Fall back to company setting - fetch shuttleBufferForArrivalTime
          const { companyId } = this.$store.state.companySettings || {};
          const { subcompanyId } = this.$route.params || {};
          const { accountType } = this.$store.state.userDetails || {};

          if (!companyId || !accountType) {
            console.warn('Missing company settings for threshold initialization, using default value');
            this.value = 5;
          } else {
            try {
              const shuttleSettings = await companies.getSettings(
                accountType,
                'shuttle-vanpool',
                companyId,
                subcompanyId,
              );

              // Ensure company setting is also a number
              const companyThreshold = Number(shuttleSettings?.shuttleBufferForArrivalTime);
              if (companyThreshold && !Number.isNaN(companyThreshold)) {
                this.value = companyThreshold;
              } else {
                console.warn('Company shuttle buffer setting not found or invalid, using default value');
                this.value = 5;
              }
            } catch (companySettingsError) {
              console.error('Error fetching company settings for threshold:', companySettingsError);
              this.value = 5;
            }
          }
        }

        this.isInitialized = true;
        // `ready` rather than `change`: the parent has to tell the resolved initial value
        // apart from a user edit, so it can make the first request instead of a second one.
        this.$emit('ready', this.value);
      } catch (error) {
        console.error('Error loading threshold preference from user preferences:', error);
        // Fall back to default value
        this.value = 5;
        this.isInitialized = true;
        this.$emit('ready', this.value);
      }
    },

    async handleChange() {
      if (!this.isInitialized) return;

      // Ensure value is a number
      const numericValue = Number(this.value);

      if (Number.isNaN(numericValue)) {
        console.error('Invalid threshold value:', this.value);
        return;
      }

      this.$emit('change', numericValue);

      // Save user preference to store
      try {
        await this.savePreference({
          key: this.preferenceKey,
          value: numericValue,
        });
      } catch (error) {
        console.error('Error saving threshold preference:', error);
      }
    },
  },
};
</script>

<style scoped>
.otp-threshold-slider {
  padding: 10px;
  background: #f8f9fa;
  border-radius: 6px;
}

.form-label {
  font-weight: 500;
  font-size: 0.875rem;
}

.form-range {
  width: 100%;
}

.min-badge {
  width: 50px;
}
</style>
